import React, { useState } from 'react';

import { Form, Card, Col, Row, Empty, Button, message, notification, Typography } from 'antd';

import { FileUnknownOutlined, ArrowLeftOutlined } from '@ant-design/icons';

import { Price, ErrorRender } from '@partsbadger/library';

import QuoteStore from '../../../stores/QuoteStore';
import UserStore from '../../../stores/UserStore';
import SalesOrderStore from '../../../stores/SalesOrderStore';

import { PageHeader } from 'components/PageHeader';

import { Link, useHistory } from 'react-router-dom';

import { observer } from 'mobx-react';

const { Title } = Typography;

const Cart: React.FC = () => {
    const [loading, setLoading] = useState<boolean>(false);

    const history = useHistory();

    const goCheckoutPage = async () => {
        setLoading(true);

        if (!UserStore.user?.default_contact?.id) {
            setLoading(false);
            return message.error('No contact provided');
        }

        const payload = {
            contact: UserStore.user.default_contact.id,
            line_items: QuoteStore.selectedLineItems?.map(item => {
                return {
                    quantity: item.quantity,
                    name: item.name,
                    quote_has_product: item.id,
                    product_description: '',
                    unit_price: item.unit_price,
                };
            }),
        };

        await SalesOrderStore.createOrderDraft(payload)
            .then(order => {
                history.push(`/my-account/orders/checkout/${order.id}/`);
            })
            .catch(error => {
                notification.error({
                    duration: 10,
                    message: 'Error',
                    description: <ErrorRender error={error} />,
                    placement: 'bottomRight',
                });

                setLoading(false);
            });
    };

    const sumSubTotal = QuoteStore.selectedLineItems?.reduce(
        (accumulator, currentValue) => accumulator + parseFloat(currentValue.total_price),
        0
    );

    const handleDelete = (quoteIndex: number, quoteId: number) => {
        QuoteStore.removeSelectedLineItems(quoteIndex, quoteId);
        notification.success({
            duration: 3,
            message: 'Part remove from the Cart',
            description: 'Your item has been removed from the cart.',
            placement: 'topRight',
        });
    };

    return (
        <>
            <PageHeader>
                <Title level={1}>My Cart</Title>
                <Button
                    type="link"
                    icon={<ArrowLeftOutlined />}
                    onClick={() => {
                        history.push('/my-account/parts');
                    }}
                    className="text-primary m-0 p-0"
                >
                    Back to Parts List
                </Button>
            </PageHeader>
            <div className="container">
                {!QuoteStore?.selectedLineItems?.length ? (
                    <>
                        <Empty image={null} description={<span className="font-size-lg">Your Cart is Empty</span>}>
                            <Link to="/my-account/parts" className="ant-btn ant-btn-primary">
                                Select a Part
                            </Link>
                        </Empty>
                    </>
                ) : (
                    <Row gutter={[16, 0]}>
                        <Col xl={16} lg={14} span={24}>
                            {QuoteStore.selectedLineItems?.map((item, index) => {
                                // Generate a string that changes on every update to use as a trigger for
                                // re-rendering the ThreeJS thumbnail

                                const d = new Date();
                                const seed = String(d.getTime());

                                return (
                                    <Card className="shadow-sm mb-2" key={item.id} bodyStyle={{ padding: '1rem' }}>
                                        <div className="d-flex flex-xl-row flex-column justify-content-between w-100">
                                            <div className="d-flex flex-row">
                                                <div className="mr-3">
                                                    {item?.product?.thumbnail ? (
                                                        <div className="d-flex flex-column justify-content-center align-items-center">
                                                            <img
                                                                className="thumbnail-3d-file"
                                                                style={{
                                                                    width: '200px',
                                                                    height: '130px',
                                                                    display: 'flex',
                                                                    margin: 'auto',
                                                                    justifyContent: 'center',
                                                                }}
                                                                src={item?.product?.thumbnail}
                                                            />
                                                        </div>
                                                    ) : (
                                                        <FileUnknownOutlined
                                                            style={{
                                                                fontSize: 120,
                                                                display: 'flex',
                                                                margin: 'auto',
                                                                justifyContent: 'center',
                                                            }}
                                                        />
                                                    )}
                                                </div>
                                                <div className="d-flex flex-column justify-content-between">
                                                    <div className="d-flex flex-column">
                                                        <span className="font-size-md font-weight-semibold mb-2">
                                                            {item.name}
                                                        </span>
                                                        <span>
                                                            <span className="font-weight-semibold">Material:</span>{' '}
                                                            <span>{item.product.material_related.name}</span>
                                                        </span>
                                                        <span>
                                                            <span className="font-weight-semibold">Finish:</span>{' '}
                                                            <span>{item.product.finish_related.name}</span>
                                                        </span>
                                                        <span>
                                                            <span className="font-weight-semibold">Coating:</span>{' '}
                                                            <span>{item.product.coating_related.name}</span>
                                                        </span>
                                                    </div>
                                                    <div
                                                        className="d-flex flex-row align-items-center"
                                                        style={{ columnGap: '6px' }}
                                                    >
                                                        <span
                                                            className="link underline text-red-600"
                                                            onClick={() => {
                                                                handleDelete(index, item.id);
                                                            }}
                                                        >
                                                            Remove
                                                        </span>
                                                    </div>
                                                </div>
                                            </div>

                                            <div className="d-flex flex-xl-column-reverse flex-row text-right justify-content-between align-items-end">
                                                <div className="quantity-selector d-flex flex-row align-items-center justify-content-end">
                                                    <div className="d-flex flex-row align-items-center">
                                                        <label
                                                            htmlFor="quantity-input"
                                                            className="ant-form-item-label mr-2"
                                                        >
                                                            Qty:
                                                        </label>
                                                        <div className="button-group">
                                                            <Form.Item noStyle>
                                                                <span>{item.quantity}</span>
                                                            </Form.Item>
                                                        </div>
                                                    </div>
                                                </div>
                                                <div
                                                    className="d-flex flex-column justify-content-center ml-3 mb-xl-3"
                                                    style={{ minWidth: '100px' }}
                                                >
                                                    <span
                                                        style={{
                                                            fontSize: '1rem',
                                                            fontWeight: 400,
                                                            paddingLeft: '0.5rem',
                                                        }}
                                                    >
                                                        <Price value={item.unit_price} /> ea.
                                                    </span>
                                                    <span
                                                        style={{
                                                            fontSize: '1.3rem',
                                                            fontWeight: 500,
                                                            paddingLeft: '0.5rem',
                                                        }}
                                                    >
                                                        <Price value={item.total_price} />
                                                    </span>
                                                </div>
                                            </div>
                                        </div>
                                    </Card>
                                );
                            })}
                        </Col>
                        <Col xl={8} lg={10} span={24}>
                            <Card
                                title={
                                    <div className="d-flex flex-row justify-content-between">
                                        <span className="font-size-lg font-weight-normal">
                                            Subtotal ({QuoteStore.selectedLineItems?.length}{' '}
                                            {QuoteStore.selectedLineItems?.length === 1 ? 'Item' : 'Items'}
                                            ):
                                        </span>
                                        <span className="font-size-lg font-weight-semibold">
                                            <Price value={sumSubTotal} />
                                        </span>
                                    </div>
                                }
                                className="shadow-sm"
                            >
                                <Button
                                    id="quickturn-continue-to-checkout"
                                    type="primary"
                                    size="large"
                                    block
                                    onClick={async () => {
                                        await goCheckoutPage();
                                    }}
                                >
                                    Proceed to Checkout
                                </Button>
                            </Card>
                        </Col>
                    </Row>
                )}
            </div>
        </>
    );
};

export default observer(Cart);
